/**
 * Plant Loop Type Definitions
 *
 * Type definitions for plant loop components, nodes, and edges
 * used in the visual plant loop editor.
 */

import type { FluidMedium, HVACPort } from '../hvac/types';

/**
 * Plant loop types supported in the editor
 */
export type PlantLoopType =
  | 'chilled-water'
  | 'hot-water'
  | 'condenser-water'
  | 'ground-loop'
  | 'service-hot-water';

/**
 * Side of the plant loop (supply or demand)
 */
export type PlantLoopSide = 'supply' | 'demand';

/**
 * Plant component categories
 */
export type PlantComponentCategory =
  | 'chillers'
  | 'boilers'
  | 'cooling-towers'
  | 'heat-exchangers'
  | 'pumps'
  | 'distribution'
  | 'coils';

/**
 * Plant port definition extending HVAC port
 */
export interface PlantPort extends HVACPort {
  /** Which side of the loop this port belongs to */
  loopSide?: PlantLoopSide;
}

/**
 * Plant component definition
 */
export interface PlantComponentDefinition {
  /** Unique component type ID */
  type: string;
  /** Display name */
  displayName: string;
  /** Category for grouping in palette */
  category: PlantComponentCategory;
  /** Icon name */
  icon: string;
  /** IDF object class name */
  idfClass: string;
  /** Description */
  description: string;
  /** Port definitions */
  ports: PlantPort[];
  /** Default field values */
  defaults: Record<string, string>;
  /** Component color */
  color: string;
  /** Which loop types this component can be used in */
  allowedLoopTypes: PlantLoopType[];
  /** Whether this component can serve as a supply-side equipment */
  supplyEquipment?: boolean;
  /** Whether this component can serve as a demand-side equipment */
  demandEquipment?: boolean;
}

/**
 * Plant loop configuration
 */
export interface PlantLoopConfig {
  /** Loop name */
  name: string;
  /** Loop type */
  type: PlantLoopType;
  /** Fluid type (water, glycol, etc.) */
  fluidType: string;
  /** Design loop exit temperature (C) */
  designExitTemp: number;
  /** Design loop delta T (C) */
  designDeltaT: number;
  /** Minimum loop flow rate */
  minFlowRate: string;
  /** Maximum loop flow rate */
  maxFlowRate: string;
  /** Load distribution scheme */
  loadDistribution: 'Optimal' | 'SequentialLoad' | 'UniformLoad';
}

/**
 * Plant loop node data for React Flow
 */
export interface PlantNodeData {
  /** Reference to component definition type */
  componentType: string;
  /** User-defined name */
  name: string;
  /** Which side of the loop (supply or demand) */
  loopSide: PlantLoopSide;
  /** Branch index within the side */
  branchIndex: number;
  /** Reference to IDF object ID */
  idfObjectId?: string;
  /** Component-specific field values */
  fields: Record<string, string>;
  /** Validation errors */
  hasErrors: boolean;
  errors: string[];
}

/**
 * Plant loop edge data
 */
export interface PlantEdgeData {
  /** Source port ID */
  sourcePort: string;
  /** Target port ID */
  targetPort: string;
  /** Fluid medium */
  medium: FluidMedium;
  /** Auto-generated node name for EnergyPlus */
  nodeName: string;
  /** Which loop side this edge is on */
  loopSide: PlantLoopSide;
}

/**
 * Plant loop branch representation
 */
export interface PlantBranch {
  /** Branch ID */
  id: string;
  /** Branch name */
  name: string;
  /** Which side of the loop */
  side: PlantLoopSide;
  /** Component node IDs in this branch (in flow order) */
  componentIds: string[];
  /** Inlet node name */
  inletNodeName: string;
  /** Outlet node name */
  outletNodeName: string;
}

/**
 * Complete plant loop state
 */
export interface PlantLoop {
  /** Loop ID */
  id: string;
  /** Loop configuration */
  config: PlantLoopConfig;
  /** Supply side branches */
  supplyBranches: PlantBranch[];
  /** Demand side branches */
  demandBranches: PlantBranch[];
  /** Supply inlet node name */
  supplyInletNode: string;
  /** Supply outlet node name */
  supplyOutletNode: string;
  /** Demand inlet node name */
  demandInletNode: string;
  /** Demand outlet node name */
  demandOutletNode: string;
}

/**
 * Plant loop validation result
 */
export interface PlantLoopValidation {
  valid: boolean;
  errors: PlantValidationError[];
  warnings: PlantValidationWarning[];
}

/**
 * Plant loop validation error
 */
export interface PlantValidationError {
  code: string;
  message: string;
  nodeId?: string;
  loopSide?: PlantLoopSide;
}

/**
 * Plant loop validation warning
 */
export interface PlantValidationWarning {
  code: string;
  message: string;
  nodeId?: string;
  suggestion?: string;
}

/**
 * Cross-loop connection (e.g., cooling coil in air loop connected to chilled water loop)
 */
export interface CrossLoopConnection {
  /** ID of this connection */
  id: string;
  /** Air loop component ID (e.g., the cooling coil) */
  airLoopComponentId: string;
  /** Air loop component name */
  airLoopComponentName: string;
  /** Plant loop ID this connects to */
  plantLoopId: string;
  /** Plant loop name */
  plantLoopName: string;
  /** Demand branch ID in the plant loop */
  demandBranchId: string;
  /** Connection type */
  connectionType: PlantLoopType;
}

/**
 * Loop colors for visualization
 */
export const LOOP_COLORS: Record<PlantLoopType, { primary: string; secondary: string }> = {
  'chilled-water': { primary: '#1E88E5', secondary: '#64B5F6' },
  'hot-water': { primary: '#E53935', secondary: '#EF9A9A' },
  'condenser-water': { primary: '#8E24AA', secondary: '#CE93D8' },
  'ground-loop': { primary: '#43A047', secondary: '#A5D6A7' },
  'service-hot-water': { primary: '#FB8C00', secondary: '#FFCC80' },
};

/**
 * Loop type display names
 */
export const LOOP_TYPE_NAMES: Record<PlantLoopType, string> = {
  'chilled-water': 'Chilled Water Loop',
  'hot-water': 'Hot Water Loop',
  'condenser-water': 'Condenser Water Loop',
  'ground-loop': 'Ground Loop',
  'service-hot-water': 'Service Hot Water Loop',
};

/**
 * Default loop configurations by type
 */
export const DEFAULT_LOOP_CONFIGS: Record<PlantLoopType, Partial<PlantLoopConfig>> = {
  'chilled-water': {
    fluidType: 'Water',
    designExitTemp: 6.67, // 44F
    designDeltaT: 5.56, // 10F delta
    loadDistribution: 'Optimal',
  },
  'hot-water': {
    fluidType: 'Water',
    designExitTemp: 82.2, // 180F
    designDeltaT: 11.1, // 20F delta
    loadDistribution: 'Optimal',
  },
  'condenser-water': {
    fluidType: 'Water',
    designExitTemp: 29.4, // 85F
    designDeltaT: 5.56, // 10F delta
    loadDistribution: 'SequentialLoad',
  },
  'ground-loop': {
    fluidType: 'PropyleneGlycol',
    designExitTemp: 15.6, // 60F
    designDeltaT: 5.56,
    loadDistribution: 'Optimal',
  },
  'service-hot-water': {
    fluidType: 'Water',
    designExitTemp: 60, // 140F
    designDeltaT: 16.7, // 30F delta
    loadDistribution: 'SequentialLoad',
  },
};
